/**
 * ============================================================================
 * FILTER CONFIG — properties mirror the CTS QuickSight WBR dashboard.
 * ============================================================================
 *
 * Source of truth: CollectionTrackerServiceCDK lib/quicksightStack.ts,
 * executive-summary-production sheet. QuickSight populates every dimension
 * dropdown FROM THE DATA (cascading, auto-populated); only Report Period and
 * Metric Group have static value lists. We follow the same rule:
 *
 * - Dimension VALUES come from useFilterOptions (src/features/api/
 *   filterOptions.ts) — distinct values from the cached getMetrics rowset.
 *   DO NOT hardcode dimension values here; a hardcoded value that doesn't
 *   exist in data silently filters everything to zero rows.
 * - STATIC_FILTER_OPTIONS holds only the QuickSight-static lists.
 *
 * QuickSight controls NOT yet supported:
 *   Metric Name cascade, DCR Created Date Range.
 *
 * Customer Type and the Backup/BYOC/OTS/BYOT-Offline flags ARE row filters
 * now — the API sends customerType and the is*Dcr booleans, so the default
 * chips below constrain rows exactly as QuickSight's default state does.
 */

export const FILTER_PROPERTIES = [
  // Static-valued controls (QuickSight parity: SINGLE_SELECT static lists)
  { key: 'reportPeriod', operators: ['='], propertyLabel: 'Report Period', groupValuesLabel: 'Report Period values' },
  // Multi-select in the live dashboard; scopes the volume summary visuals only
  // (the OTD visuals have fixed rows). No 'OTD Metrics' value — the control
  // does not offer one.
  { key: 'metricGroup', operators: ['=', '!='], propertyLabel: 'Metric Group', groupValuesLabel: 'Metric Group values' },
  // QuickSight default-exclusion controls. These constrain rows client-side
  // (accessors in filterPredicate.ts) — the API sends customerType and the
  // is*Dcr flags, so the default state reproduces QuickSight's exclusions.
  { key: 'customerType', operators: ['=', '!='], propertyLabel: 'Customer Type', groupValuesLabel: 'Customer Type values' },
  { key: 'backupDcr', operators: ['='], propertyLabel: 'Backup DCR', groupValuesLabel: 'Backup DCR values' },
  { key: 'byocDcr', operators: ['='], propertyLabel: 'BYOC DCR', groupValuesLabel: 'BYOC DCR values' },
  { key: 'otsDcr', operators: ['='], propertyLabel: 'OTS DCR', groupValuesLabel: 'OTS DCR values' },
  { key: 'byotOfflineDcr', operators: ['='], propertyLabel: 'BYOT/Offline DCR', groupValuesLabel: 'BYOT/Offline DCR values' },
  // Data-driven dimension controls (values from the fetched rowset)
  { key: 'orderCategory', operators: ['=', '!='], propertyLabel: 'Order Category', groupValuesLabel: 'Order Category values' },
  { key: 'customer', operators: ['=', '!=', ':', '!:'], propertyLabel: 'Customer', groupValuesLabel: 'Customer values' },
  { key: 'locale', operators: ['=', '!='], propertyLabel: 'Locale', groupValuesLabel: 'Locale values' },
  { key: 'channel', operators: ['=', '!='], propertyLabel: 'Channel', groupValuesLabel: 'Channel values' },
  { key: 'subChannel', operators: ['=', '!='], propertyLabel: 'Sub Channel', groupValuesLabel: 'Sub Channel values' },
  { key: 'domain', operators: ['=', '!=', ':', '!:'], propertyLabel: 'Domain', groupValuesLabel: 'Domain values' },
  { key: 'dcrStatus', operators: ['=', '!='], propertyLabel: 'DCR Status', groupValuesLabel: 'DCR Status values' },
  { key: 'deliveryType', operators: ['=', '!='], propertyLabel: 'Delivery Type', groupValuesLabel: 'Delivery Type values' },
  { key: 'uom', operators: ['=', '!='], propertyLabel: 'UoM', groupValuesLabel: 'Unit of Measure values' },
  { key: 'dcrId', operators: ['=', '!=', ':', '!:'], propertyLabel: 'DCR ID', groupValuesLabel: 'DCR ID values' },
  // Parameter-style control (not a row filter; consumed by OTD calc)
  { key: 'onTimeDeliveryThreshold', operators: ['=', '>=', '<='], propertyLabel: 'OTD Threshold %', groupValuesLabel: 'OTD Threshold values' },
];

/**
 * ONLY the QuickSight-static lists. Everything else is data-driven — see
 * useFilterOptions.
 */
export const STATIC_FILTER_OPTIONS = [
  { propertyKey: 'reportPeriod', value: 'WBR' },
  { propertyKey: 'reportPeriod', value: 'MBR' },
  { propertyKey: 'reportPeriod', value: 'QBR' },
  { propertyKey: 'customerType', value: 'AGIDS' },
  { propertyKey: 'customerType', value: 'Internal' },
  { propertyKey: 'backupDcr', value: 'No' },
  { propertyKey: 'backupDcr', value: 'Yes' },
  { propertyKey: 'byocDcr', value: 'No' },
  { propertyKey: 'byocDcr', value: 'Yes' },
  { propertyKey: 'otsDcr', value: 'No' },
  { propertyKey: 'otsDcr', value: 'Yes' },
  { propertyKey: 'byotOfflineDcr', value: 'No' },
  { propertyKey: 'byotOfflineDcr', value: 'Yes' },
  { propertyKey: 'metricGroup', value: 'WBR Metrics Only' },
  { propertyKey: 'metricGroup', value: 'Core Business Metrics' },
  { propertyKey: 'metricGroup', value: 'Operational Metrics' },
  { propertyKey: 'metricGroup', value: 'Dive Deep Metrics' },
  { propertyKey: 'onTimeDeliveryThreshold', value: '90' },
  { propertyKey: 'onTimeDeliveryThreshold', value: '95' },
  { propertyKey: 'onTimeDeliveryThreshold', value: '99' },
];

/**
 * @deprecated Dimension values are data-driven now (useFilterOptions).
 * Kept as an alias so stale imports fail loudly in review, not silently
 * at runtime with wrong values.
 */
export const FILTER_OPTIONS = STATIC_FILTER_OPTIONS;

/**
 * Default chips — full QuickSight default-state parity (from the live WBR
 * dashboard control bar): Report Period=WBR, Order Category=Production,
 * Customer Type=AGIDS, Backup/BYOC/OTS/BYOT-Offline DCR=No, Metric
 * Group=WBR Metrics Only. The DCR chips filter rows; metricGroup filters
 * metric ROWS and — matching the live dashboard — scopes only the volume
 * summary visuals. The On Time Delivery % visuals have fixed rows and ignore
 * it (verified against the CTS QuickSight dashboard: with Metric Group set to
 * Dive Deep Metrics, the OTD table still renders all its rows).
 */
export const DEFAULT_FILTER_QUERY = {
  tokens: [
    { propertyKey: 'reportPeriod', operator: '=', value: 'WBR' },
    { propertyKey: 'orderCategory', operator: '=', value: 'Production' },
    { propertyKey: 'customerType', operator: '=', value: 'AGIDS' },
    { propertyKey: 'backupDcr', operator: '=', value: 'No' },
    { propertyKey: 'byocDcr', operator: '=', value: 'No' },
    { propertyKey: 'otsDcr', operator: '=', value: 'No' },
    { propertyKey: 'byotOfflineDcr', operator: '=', value: 'No' },
    { propertyKey: 'metricGroup', operator: '=', value: 'WBR Metrics Only' },
  ],
  operation: 'and' as const,
};
